'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { AlertTriangle, Check, ImagePlus, StickyNote } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { useConfirmBuildingAngle } from '@/shared/api/hooks/use-project-angles';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { DIFFERENT_BUILDING_REASON } from '@/types/project-angle';
import { AngleImageWithProgress } from '@/features/design/render';
import { useRenderStore } from '@/features/design/render/model/render-form.store';
import { ConceptActions, RenderInfoSheet } from '@/features/design/concept';
import { AngleImageFrame } from './AngleImageFrame';
import { LlmFeedbackImageOverlay } from './LlmFeedbackImageOverlay';
import {
  RenderFeedbackChips,
  RenderFeedbackBanners,
} from '@/features/design/render/ui/RenderFeedbackGroup';
import { useOptionalDesktopLayout } from '@/features/project';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import type { ImageSizingConstraints } from '@/shared/hooks';
import type { ConceptAngle, Angle } from '@/types/concept';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';
import type { Render } from '@/types/render';
import type { Job } from '@/features/design/job-queue';
import { NoticeBanner } from '@/shared/ui';

interface MainAngleDisplayProps {
  project: Project;
  concept: Concept;
  mainAngle: ConceptAngle;
  angles: Record<string, Angle>;
  currentRender: Render | undefined;
  renderFileUrl: string | undefined;
  shouldShowRender: boolean;
  isRendering: boolean;
  activeRenderJob: Job | undefined;
  activeUpscaleJob: Job | undefined;
  /** Just-completed render still prefetching its image — holds the progress
   *  overlay until it is decoded (Apple/Android parity). */
  isPreloadingRender?: boolean;
  /** Pulse trigger for the positive LLM-feedback icon — see LlmFeedbackImageOverlay. */
  feedbackPulseSignal?: string | null;
  hideRenderButton: boolean;
  onToggleShowRender: () => void;
  onDraggingSliderChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onStartNewPhoto: () => void;
  onReplacePhoto: () => void;
  onEditNotes?: () => void;
  onRenameAngle?: () => void;
  onEditAngles?: () => void;
  onDeleteConcept?: () => void;
  onDeleteRender?: (renderId: string) => void;
  onSaveAsReference?: () => void;
  isSavingAsReference?: boolean;
  /** Per-layout sizing constraints (Apple-parity caps/floors/lift). */
  sizing: ImageSizingConstraints;
  isArchived?: boolean;
}

export function MainAngleDisplay({
  project,
  concept,
  mainAngle,
  angles,
  currentRender,
  renderFileUrl,
  shouldShowRender,
  isRendering,
  activeRenderJob,
  activeUpscaleJob,
  isPreloadingRender = false,
  onToggleShowRender,
  onDraggingSliderChange,
  onSwipeLeft,
  onSwipeRight,
  onStartNewPhoto,
  onReplacePhoto,
  onEditNotes,
  onRenameAngle,
  onEditAngles,
  onDeleteConcept,
  onDeleteRender,
  onSaveAsReference,
  isSavingAsReference = false,
  feedbackPulseSignal,
  sizing,
  isArchived = false,
}: MainAngleDisplayProps) {
  const tAngle = useTranslations('angle.menu');
  const tOptions = useTranslations('render.options');
  const router = useRouter();
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);

  // Two-column (xl+ inspector) layout keeps LLM feedback as expanded banners
  // below the image (collapsible to in-flow chips). Single-column collapses it
  // to icons pinned on the image — there's no room for open banners in the
  // stacked image+settings scroll. Mirrors `AngleImageFrame`'s own signal.
  const desktopLayout = useOptionalDesktopLayout();
  const { isXl } = useBreakpoint();
  const isTwoColumnLayout = !!(isXl && desktopLayout);
  const showFeedbackForRender =
    currentRender && !isRendering && !activeRenderJob;

  // The single-angle viewer is mode-driven in BOTH arrangements (two-column
  // inspector and single-column stacked) — there is one angle on screen and it
  // switches as the user toggles Explore/Refine, so the toggle no longer needs
  // its own duplicate preview (see UnifiedRenderPanel `hideTogglePreview`).
  // Slider clip semantics (LTR):
  //   position = 0   → afterImage (render) fully visible
  //   position = 100 → beforeImage (source) fully visible
  // So Refine → 0 (show render), Recreate/Explore → 100 (show source photo).
  // Animation matches RenderModeToggle (450ms cubic-bezier). User can still
  // drag manually — drag flips the slider into an override state that resets
  // only when the mode prop changes again.
  const renderMode = useRenderStore((state) => state.formData.mode);
  const sliderProgrammaticPosition = renderMode === 'refine' ? 0 : 100;

  const angleData = mainAngle.relationships?.angle?.data?.id
    ? angles[mainAngle.relationships.angle.data.id]
    : undefined;
  const angleMeta = mainAngle.relationships?.angle?.meta;
  const angleFileUrl = angleData?.attributes?.file?.url || angleMeta?.file_url;

  // Seed the container shape from the API-known angle dimensions (concept-angle
  // meta, with the full Angle resource's file dims as a fallback) so it doesn't
  // reflow from 16:9 once the browser decodes the image.
  const angleWidth = angleMeta?.width ?? angleData?.attributes?.file?.width;
  const angleHeight = angleMeta?.height ?? angleData?.attributes?.file?.height;
  const initialAspectRatio =
    angleWidth && angleHeight && angleHeight > 0
      ? angleWidth / angleHeight
      : undefined;

  const angleValidation = mainAngle.relationships?.angle?.meta?.validation;
  const needsReplacement =
    angleValidation?.is_deleted || angleValidation?.can_replace;
  // Strict different-building model (cross-app parity): a different-building main
  // angle offers ONLY "It's my building" (confirm-building → valid view) beside
  // the usual replace affordance — no "Create new building". Apple/Android
  // dropped that affordance from their concept-editor strict surfaces too.
  const isDifferentBuilding =
    angleValidation?.validation_failure_reason === DIFFERENT_BUILDING_REASON;
  // "It's my building" override — shown when the server says the false rejection
  // can be lifted. POST /angles/{id}/confirm-building flips the angle back to
  // Valid; the invalidated concept/project queries clear this banner.
  const canOverrideBuilding =
    isDifferentBuilding && angleValidation?.can_override_building === true;
  const angleId = mainAngle.relationships?.angle?.data?.id;
  const confirmBuilding = useConfirmBuildingAngle(project.id);
  const handleConfirmBuilding = () => {
    if (!angleId) return;
    confirmBuilding.mutate(angleId, {
      onError: (error) => {
        toast.error(
          error instanceof ApiClientError
            ? error.getFirstError()
            : tOptions('confirmBuildingError')
        );
      },
    });
  };

  // Invalid angle with no image — show replacement placeholder
  if (!angleFileUrl && !renderFileUrl && !activeRenderJob) {
    if (needsReplacement) {
      return (
        <div className="relative mb-4">
          <div className="relative border border-border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900">
            <div className="aspect-[16/9] flex flex-col items-center justify-center gap-3 p-6">
              <AlertTriangle className="h-10 w-10 text-destructive" />
              {angleValidation?.recommendations && (
                <p className="text-form-help text-muted-foreground text-center max-w-md">
                  {angleValidation.recommendations}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {canOverrideBuilding && (
                  <Button
                    variant="default"
                    onClick={handleConfirmBuilding}
                    disabled={confirmBuilding.isPending}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {tOptions('itsMyBuilding')}
                  </Button>
                )}
                <Button
                  variant={canOverrideBuilding ? 'outline' : 'default'}
                  onClick={onReplacePhoto}
                  disabled={confirmBuilding.isPending}
                >
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {tOptions('replaceSourcePhoto')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  // Actions bar (warning icon + menu). Each button keeps a 44px hit area
  // with a visible 28px circle painted via `before:` pseudo-element — same
  // construction as the name/refine pills above. Pills sit flush with the
  // top-right corner; the extra hit area extends into the corner.
  const actionsBarContent = !concept.attributes.is_main_angle_generated && (
    <div className="flex items-center">
      {/* Filtered-content feedback now lives as a tappable icon pinned to the
          image's bottom-center (LlmFeedbackImageOverlay) — no separate warning
          button / scroll-to-banner affordance. */}
      <ConceptActions
        projectId={project.id}
        conceptId={concept.id}
        conceptName={
          concept.attributes.title ||
          project.attributes?.name ||
          'Concept'
        }
        conceptSequenceNumber={concept.attributes.project_sequence_number}
        mode="editor"
        isDerivative={false}
        hasRender={!!currentRender}
        renderId={currentRender?.id}
        renderFileUrl={renderFileUrl}
        isUpscaled={currentRender?.attributes?.is_upscaled || false}
        onStartNewPhoto={onStartNewPhoto}
        onReplacePhoto={onReplacePhoto}
        onRenameAngle={onRenameAngle}
        onEditAngles={onEditAngles}
        onDeleteConcept={onDeleteConcept}
        onDeleteRender={onDeleteRender}
        onSaveAsReference={onSaveAsReference}
        isSavingAsReference={isSavingAsReference}
        onShowRenderInfo={
          currentRender ? () => setIsInfoSheetOpen(true) : undefined
        }
        className="[&_svg]:text-[#545454] before:bg-white/35 before:backdrop-blur-sm"
      />
    </div>
  );

  return (
    <div>
      {/* Main angle image or render. Frame styling (settings-card surface in
          two-column, tight border in single-column) and auto vertical padding
          live in AngleImageFrame so derivative angles get identical treatment. */}
      <AngleImageFrame className="mb-4">
        {/* Phone (< sm): square corners — the card rides full-bleed (see
            AngleImageFrame). `sm`+ keeps the rounded panel. */}
        <div className="relative rounded-none sm:rounded-lg overflow-hidden">
          {/* Top-leading overlay row: collapsed LLM-feedback icons
              (single-column only). The custom view name is no longer painted on
              the image — it lives in the angle tab label now (see
              ConceptContentTabs). Rename stays in the ⋯ menu (ConceptActions). */}
          {!isTwoColumnLayout && showFeedbackForRender && (
            <div className="absolute top-0 start-0 z-10 flex items-center">
              <LlmFeedbackImageOverlay
                render={currentRender}
                pulseSignal={feedbackPulseSignal}
              />
            </div>
          )}
          {angleFileUrl || renderFileUrl ? (
            <AngleImageWithProgress
              sourceUrl={angleFileUrl}
              renderUrl={renderFileUrl}
              alt={
                mainAngle.relationships?.angle?.meta?.name ||
                tAngle('mainAngle')
              }
              showingRender={shouldShowRender}
              onToggle={onToggleShowRender}
              isUpscaled={currentRender?.attributes?.is_upscaled || false}
              activeRenderJob={activeRenderJob}
              activeUpscaleJob={activeUpscaleJob}
              isPreloadingRender={isPreloadingRender}
              conceptId={concept.id}
              projectId={project.id}
              onDraggingChange={onDraggingSliderChange}
              onSwipeLeft={onSwipeLeft}
              onSwipeRight={onSwipeRight}
              onImageClick={() => setIsViewerOpen(true)}
              isViewerOpenExternal={isViewerOpen}
              onViewerClose={() => setIsViewerOpen(false)}
              sizing={sizing}
              sliderProgrammaticPosition={sliderProgrammaticPosition}
              initialAspectRatio={initialAspectRatio}
            />
          ) : (
            /* Show placeholder if rendering but no image yet */
            <div className="aspect-[16/9] bg-gray-50 dark:bg-gray-900" />
          )}

          {actionsBarContent && (
            <div className="absolute top-0 end-0">
              {actionsBarContent}
            </div>
          )}

        </div>
      </AngleImageFrame>

      {/* Concept note — render-adjacent banner in flow. The render button moved
          to the floating Generate capsule (single-column) / inspector footer
          (two-column), the favorite moved to the page header. */}
      {concept.attributes.note && !isArchived && (
        <button
          onClick={onEditNotes}
          className="mt-2 w-full text-left cursor-pointer hover:opacity-90 transition-opacity"
          type="button"
        >
          <NoticeBanner
            variant="info"
            icon={StickyNote}
            message={concept.attributes.note}
            titleWeight="font-normal"
          />
        </button>
      )}

      {/* LLM feedback (two-column) — expanded banners below the image (after the
          note, matching Apple/Android), collapsible to in-flow chips above
          them. Single-column shows icons pinned on the image instead. */}
      {isTwoColumnLayout && showFeedbackForRender && (
        <div className="mt-2 flex flex-col gap-2">
          <RenderFeedbackChips render={currentRender} />
          <RenderFeedbackBanners render={currentRender} />
        </div>
      )}

      {/* Render-info sheet — opened via the "Information" item in the
          angle's overflow menu. */}
      {currentRender && (
        <RenderInfoSheet
          render={currentRender}
          projectId={project.id}
          currentAngleId={mainAngle.id}
          open={isInfoSheetOpen}
          onOpenChange={setIsInfoSheetOpen}
          onNavigateToParent={(parentConceptId) => {
            // Main-angle refine parents always live in another concept (each
            // main-render call creates a new concept), so swap-in-place is
            // not applicable here — always navigate.
            router.push(
              `/project/${project.id}/concept/${parentConceptId}`
            );
          }}
        />
      )}
    </div>
  );
}
